// ─── 접속 IP 제한 게이트 ───
// admin_ips 컬렉션(enable==='Y')에 등록된 IP에서만 접속 허용.
// 예외: localhost(개발), RN 모바일 앱(현장 직원 떠돌이 IP), IP 조회 실패/목록 비어있음 → 허용(전체 잠김 방지)
import { db } from './firebase';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';

export function isLocalhostHost() {
  if (typeof window === 'undefined') return true;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || h === '::1' || h === '0.0.0.0';
}

// RN WebView(모바일 앱) 여부 — webviewBridge.isInRnWebView와 동일 판정
function isRnWebView() {
  if (typeof window === 'undefined') return false;
  return !!(window.ReactNativeWebView || /RNShell|PlHouseApp/i.test(navigator.userAgent || ''));
}

// 공인 IP 조회 (여러 서비스 폴백)
async function fetchPublicIp() {
  const sources = [
    async () => {
      const r = await fetch('https://api.ipify.org?format=json');
      return (await r.json()).ip;
    },
    async () => {
      const r = await fetch('https://api64.ipify.org?format=json');
      return (await r.json()).ip;
    },
    async () => {
      const r = await fetch('https://ipapi.co/json/');
      return (await r.json()).ip;
    },
  ];
  for (const get of sources) {
    try {
      const ip = await get();
      if (ip) return String(ip).trim();
    } catch (e) { /* 다음 소스 시도 */ }
  }
  return null;
}

// 전역 IP 체크 스위치 (settings/master.a_ip_check) — 'Y'=특정IP만 로그인(체크 ON), 그 외/누락=모두로그인(체크 OFF)
async function isIpCheckEnabled() {
  try {
    const snap = await getDoc(doc(db, 'settings', 'master'));
    return snap.exists() && snap.data().a_ip_check === 'Y';
  } catch (e) {
    return false; // 조회 실패 → 체크 미적용(전체 잠김 방지)
  }
}

async function fetchAllowedIps() {
  try {
    const snap = await getDocs(collection(db, 'admin_ips'));
    return snap.docs
      .map(d => d.data())
      .filter(d => (d.enable || 'Y') === 'Y')
      .map(d => String(d.ip || '').trim())
      .filter(Boolean);
  } catch (e) {
    return null; // 조회 실패 → 제한 안 함
  }
}

/**
 * 접속 허용 여부 판정.
 * @returns {Promise<{ allowed: boolean, ip: string|null, reason: string }>}
 */
export async function checkIpAccess(member) {
  // 1) 개발: localhost는 항상 허용
  if (isLocalhostHost()) return { allowed: true, ip: null, reason: 'localhost' };
  // 2) 모바일 앱(현장 직원)은 IP 제한 제외
  if (isRnWebView()) return { allowed: true, ip: null, reason: 'app' };

  // 2-4) 해당 직원의 IP 체크가 '사용중지'면 게이트 제외 — IP가 고정되지 않는 직원 개별 예외 처리
  //      (관리자가 특정 직원을 '사용중지'로 바꿔도 로그인이 IP로 막히던 문제 수정. 2026-07-17)
  if (member && member.ip_check === '사용중지') return { allowed: true, ip: null, reason: 'member-disabled' };

  // 2-5) 전역 IP 체크가 '모두로그인'(꺼짐)이면 게이트 미적용 — admin_ips 목록과 무관하게 허용
  if (!(await isIpCheckEnabled())) return { allowed: true, ip: null, reason: 'disabled' };

  const allowList = await fetchAllowedIps();
  // 3) 목록 조회 실패 → 잠김 방지 위해 허용
  if (allowList === null) return { allowed: true, ip: null, reason: 'list-error' };
  // 4) 등록된 IP가 하나도 없으면 제한 미적용(허용)
  if (allowList.length === 0) return { allowed: true, ip: null, reason: 'empty-list' };

  const myIp = await fetchPublicIp();
  // 5) 내 IP 조회 실패 → 잠김 방지 위해 허용
  if (!myIp) return { allowed: true, ip: null, reason: 'ip-error' };

  const allowed = allowList.includes(myIp);
  return { allowed, ip: myIp, reason: allowed ? 'matched' : 'blocked' };
}
